import React from "react";
import "./index.m.css";
import { useSubscriptionState } from "../utils/subscription-state";
import { SpinnerLoader } from "../../../common/loader";

import { CheckIcon } from "./icons";

const decodeHTMLEntities = (text) => {
  if (!text) return text;
  return text
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
};

export const mapAccessTypePlanToUI = (plan) => {
  let ca = {};
  if (Array.isArray(plan.custom_attributes)) {
    plan.custom_attributes.forEach(attr => {
      if (attr.name) ca[attr.name.toLowerCase()] = attr.value;
    });
  } else if (plan.custom_attributes && typeof plan.custom_attributes === 'object') {
    Object.keys(plan.custom_attributes).forEach(k => {
      ca[k.toLowerCase()] = plan.custom_attributes[k];
    });
  }

  // Features list: try description first, fallback to others
  let features = [];
  const rawDesc = ca.description || ca["plan-features"] || ca.features || ca.benefits || "";
  if (rawDesc.includes("<li>")) {
    // Basic extraction of li tags
    const matches = rawDesc.match(/<li>(.*?)<\/li>/gi);
    if (matches) {
      features = matches.map((m) => decodeHTMLEntities(m.replace(/<\/?li>/gi, "").trim()));
    }
  } else {
    features = rawDesc
      .split(/[\n,]+/)
      .map((s) => decodeHTMLEntities(s.trim()))
      .filter(Boolean);
  }

  // Duration
  let durationStr = "";
  if (plan.duration_unit) {
    const unit = plan.duration_unit.charAt(0).toUpperCase() + plan.duration_unit.slice(1);
    if (plan.duration_length > 1) {
      const pluralUnit = unit.endsWith("s") ? unit : `${unit}s`;
      durationStr = `/ ${plan.duration_length} ${pluralUnit}`;
    } else {
      durationStr = `/${unit}`;
    }
  }

  // Price Amount
  const cents = plan.discounted_price_cents || plan.price_cents || 0;
  const currencySymbol = plan.price_currency === "INR" ? "₹" : plan.price_currency === "USD" ? "$" : "";
  const priceAmountStr = cents > 0 ? `${currencySymbol}${cents / 100}` : "";

  // Original Price (Struck-through)
  let originalPriceStr = "";
  if (ca.mrp_inr && plan.price_currency === "INR") {
    originalPriceStr = `₹${ca.mrp_inr}`;
  } else if (ca.mrp_usd && plan.price_currency === "USD") {
    originalPriceStr = `$${ca.mrp_usd}`;
  }

  // Savings Text
  let savingsTextStr = "";
  if (ca.save_price_inr && plan.price_currency === "INR") {
    savingsTextStr = `₹${ca.save_price_inr} ಉಳಿಸಿ`;
  } else if (ca.save_price_usd && plan.price_currency === "USD") {
    savingsTextStr = `$${ca.save_price_usd} ಉಳಿಸಿ`;
  }

  // Description
  const descriptionStr = plan.description ? decodeHTMLEntities(plan.description.replace(/<[^>]*>?/gm, '')) : "";

  // Badge
  let badgeStr = "";
  if (ca.meta_chip || ca.badge) {
    badgeStr = ca.meta_chip || ca.badge;
  } else if (ca["is-popular"] === "yes" || ca.popular === "true") {
    badgeStr = "Most Popular";
  }

  // Daily price
  let dailyPriceStr = ca.per_day_price || "";
  const isDailyPricePrimary = ca.per_day_price_enabled === "true" || ca.per_day_price_enabled === true;
  let dailyPriceAmount = "";
  let dailyPriceDuration = "";
  let dailyPricePrefix = "";

  if (dailyPriceStr) {
    // If the input already contains "Rs. " or "₹", strip it to prevent duplication
    const cleanPrice = dailyPriceStr.replace(/Rs\.?\s*|₹/gi, "").trim();

    if (isDailyPricePrimary) {
      dailyPriceAmount = `${currencySymbol}${cleanPrice}`;
      dailyPriceDuration = `/Day`;
    } else {
      dailyPriceStr = `ದಿನ ಒಂದಕ್ಕೆ ${currencySymbol}${cleanPrice}`;
    }
  }

  return {
    id: plan.id,
    title: decodeHTMLEntities(plan.title || ""),
    description: descriptionStr,
    priceAmount: priceAmountStr,
    priceDuration: durationStr,
    duration_length: plan.duration_length,
    duration_unit: plan.duration_unit,
    originalPrice: originalPriceStr,
    savingsText: savingsTextStr,
    features,
    badge: badgeStr,
    dailyPrice: dailyPriceStr,
    isDailyPricePrimary,
    dailyPriceAmount,
    dailyPriceDuration,
    dailyPricePrefix,
    ctaText: ca["cta-text"] || ca["button-text"] || "ಈಗಲೇ ಖರೀದಿಸಿ",
    price_currency: plan.price_currency,
    price_cents: plan.price_cents,
    discounted_price_cents: plan.discounted_price_cents,
    recurring: plan.recurring,
    subscription_type: plan.subscription_type,
    metadata: plan.metadata,
    end_timestamp: plan.end_timestamp,
    created_at: plan.created_at,
  };
};

export const PlanSection = ({ plans = [], isLoadingPlans = false, onPlanSelect, userSubscriptions = [], isSubscriptionsLoaded, isUserLogin, showPaymentProcessingBanner = false }) => {
  const { getPlanCardState } = useSubscriptionState(userSubscriptions);
  const shouldWaitForSubscriptions = isUserLogin && !isSubscriptionsLoaded;

  if (isLoadingPlans || shouldWaitForSubscriptions) {
    return (
      <section styleName="planSection">
        <div styleName="planContainer">
          <SpinnerLoader />
        </div>
      </section>
    );
  }

  if (!plans || plans.length === 0) return null;

  const mappedPlans = plans.map(mapAccessTypePlanToUI);

  return (
    <section styleName="planSection">
      <div styleName="planContainer">
        {mappedPlans.map((plan) => {
          const cardState = getPlanCardState(plan.id, plan.ctaText, showPaymentProcessingBanner);
          const isActive = cardState.isDisabled;

          return (
            <div key={plan.id} styleName={isActive ? "planCard activePlanCard" : "planCard"}>
              {plan.badge && (
                <div styleName="popularBadge">{plan.badge}</div>
              )}
              <div styleName="cardHeader">
                {plan.title ? <div styleName="planTitle">{plan.title}</div> : <div />}

                <div styleName="priceRowWrapper">
                  {plan.isDailyPricePrimary && plan.dailyPriceAmount ? (
                    <div styleName="priceRow">
                      {plan.dailyPricePrefix && <span styleName="priceDuration">{plan.dailyPricePrefix}</span>}
                      <span styleName="priceAmount">{plan.dailyPriceAmount}</span>
                      {plan.dailyPriceDuration && <span styleName="priceDuration">{plan.dailyPriceDuration}</span>}
                    </div>
                  ) : (
                    plan.priceAmount && (
                      <div styleName="priceRow">
                        <span styleName="priceAmount">{plan.priceAmount}</span>
                        <span styleName="priceDuration">{plan.priceDuration}</span>
                      </div>
                    )
                  )}
                </div>

                <div styleName="savingsRowWrapper">
                  {(plan.originalPrice || plan.savingsText) && (
                    <div styleName="savingsRow">
                      {plan.originalPrice && <span styleName="originalPrice">{plan.originalPrice}</span>}
                      {plan.savingsText && <span styleName="savingsText">{plan.savingsText}</span>}
                    </div>
                  )}
                </div>

                <div styleName="dailyPriceWrapper">
                  {plan.isDailyPricePrimary && plan.dailyPrice ? (
                    <div styleName="priceDaily">ಬಿಲ್ ಮಾಡಲಾಗಿದೆ {plan.priceAmount}{plan.priceDuration}</div>
                  ) : (
                    (!plan.isDailyPricePrimary && plan.dailyPrice) ? <div styleName="priceDaily">{plan.dailyPrice}</div> : null
                  )}
                </div>
              </div>

              <div styleName="divider" />

              {plan.features && plan.features.length > 0 && (
                <ul styleName="featuresList">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} styleName="featureItem">
                      <CheckIcon color={isActive ? "#999999" : "#C78D3C"} />
                      <span styleName="featureText">{feature}</span>
                    </li>
                  ))}
                </ul>
              )}

              {plan.ctaText && (
                (() => {
                  return (
                    <button
                      styleName="ctaButton"
                      style={cardState.style}
                      disabled={cardState.isDisabled || showPaymentProcessingBanner}
                      aria-label={`Subscribe to ${plan.title}`}
                      onClick={() => !cardState.isDisabled && onPlanSelect(plan)}
                    >
                      {cardState.text}
                    </button>
                  );
                })()
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};
